import React, { useEffect, useRef } from 'react';
import { usePromptStore } from '../../../stores/promptStore';
import { AlertTriangle, ShieldAlert, X, CheckCircle, MessageSquare } from 'lucide-react';

/**
 * PromptDialog - Design System V2 (Volvo-inspired)
 * Modal de saisie texte avec glassmorphism et animations
 * Remplace window.prompt() pour cohérence Design System
 */
const PromptDialog: React.FC = () => {
  const { isOpen, options, value, setValue, handleConfirm, handleCancel } = usePromptStore();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Focus input after animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleCancel]);

  if (!isOpen || !options) return null;

  const variant = options.variant || 'info';
  const confirmText = options.confirmText || 'Confirmer';
  const cancelText = options.cancelText || 'Annuler';
  const minLength = options.minLength ?? 1;
  const isValid = value.trim().length >= minLength;

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: <ShieldAlert className="w-7 h-7" />,
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          headerBg: 'bg-gradient-to-r from-red-50 to-red-100/50',
          headerBorder: 'border-red-200/50',
          confirmButton: 'bg-red-600 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200/50',
          focusRing: 'focus:ring-red-300',
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-7 h-7" />,
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          headerBg: 'bg-gradient-to-r from-amber-50 to-amber-100/50',
          headerBorder: 'border-amber-200/50',
          confirmButton: 'bg-amber-600 hover:bg-amber-700 hover:shadow-lg hover:shadow-amber-200/50',
          focusRing: 'focus:ring-amber-300',
        };
      case 'primary':
        return {
          icon: <CheckCircle className="w-7 h-7" />,
          iconBg: 'bg-teal-100',
          iconColor: 'text-teal-600',
          headerBg: 'bg-gradient-to-r from-teal-50 to-teal-100/50',
          headerBorder: 'border-teal-200/50',
          confirmButton: 'bg-teal-600 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50',
          focusRing: 'focus:ring-teal-300',
        };
      case 'info':
      default:
        return {
          icon: <MessageSquare className="w-7 h-7" />,
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          headerBg: 'bg-gradient-to-r from-blue-50 to-blue-100/50',
          headerBorder: 'border-blue-200/50',
          confirmButton: 'bg-teal-600 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50',
          focusRing: 'focus:ring-teal-300',
        };
    }
  };

  const styles = getVariantStyles();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter to confirm
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isValid) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px) scale(0.96);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>

      {/* Overlay avec blur */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        style={{ animation: 'fadeIn 0.2s ease-out' }}
        onClick={handleCancel}
      />

      {/* Modal Card - Design V2 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-dialog-title"
        className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-400/20
                   border border-white/50 max-w-md w-full overflow-hidden"
        style={{ animation: 'slideUp 0.3s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec gradient subtil */}
        <div className={`${styles.headerBg} border-b ${styles.headerBorder} px-6 py-4`}>
          <div className="flex items-center gap-3">
            <div className={`${styles.iconBg} ${styles.iconColor} p-2.5 rounded-xl`}>
              {styles.icon}
            </div>
            <h3 id="prompt-dialog-title" className="text-lg font-semibold text-gray-900">
              {options.title}
            </h3>
          </div>

          {/* Bouton fermer */}
          <button
            onClick={handleCancel}
            className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600
                       hover:bg-white/50 rounded-xl transition-all duration-200"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenu */}
        <div className="px-6 py-5 space-y-3">
          <p className="text-gray-600 leading-relaxed text-sm">
            {options.message}
          </p>
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={options.placeholder || ''}
            rows={3}
            className={`w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                       placeholder:text-gray-400 resize-none transition-all duration-200
                       focus:ring-2 ${styles.focusRing} focus:border-transparent focus:outline-none`}
          />
        </div>

        {/* Footer avec boutons */}
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium
                       rounded-xl transition-all duration-300
                       hover:bg-gray-50 hover:border-gray-300 hover:shadow-md
                       active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`flex-1 px-5 py-2.5 ${styles.confirmButton} text-white font-medium
                       rounded-xl transition-all duration-300
                       hover:scale-[1.02] active:scale-[0.98]
                       focus:outline-none focus:ring-2 focus:ring-offset-2
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptDialog;
